import { areIntervalsOverlapping, differenceInCalendarDays, eachDayOfInterval, isSameDay, isWeekend } from 'date-fns';

import { locationArt, type Zeitraum } from '../../../../dtos';
import { getZeitraeumeStartEnd, isDateInZeitraum } from '../../../../utils/zeitraum';
import {
  type CalendarEvent,
  type CalendarRaumEvent,
  isExperteEvent,
  isGruppenraumEvent,
  isSeminarraumEvent,
  isStandortEvent,
} from './CalendarEvent';
import { type CalendarEventState, calendarEventStates } from './CalendarEvent/calendarEventStates';
import {
  type AbstractCalendarResource,
  type CalendarExperteResource,
  type CalendarSeminarraumResource,
  isSeminarraumResource,
  isStandortResource,
} from './CalendarResource';
import { eventCreationStrategies } from './CalendarResource/AbstractCalendarResource.types';
import { calendarResourceTypes } from './calendarResourceTypes';

const errors = {
  resourceNotFound: 'Ressource nicht gefunden.',
  containsWeekend: 'Planung enthält ein Wochenende',
  unselectGruppenraumBeforeSeminarraumChange:
    'Der Seminarraum kann nicht zu einer anderen Location gewechselt werden, da noch Gruppenräume ausgewählt sind. Bitte diese zuerst abwählen.',
  unselectGruppenraumBeforeStandortChange:
    'Es kann nicht auf Standortblockung gewechselt werden, da noch Gruppenräume ausgewählt sind. Bitte diese zuerst abwählen.',
  gruppenraumSeminarraumMismatch: (locationKuerzel: string) =>
    `Der Gruppenraum muss an der selben Location wie der Seminarraum liegen: "${locationKuerzel}".`,
  seminarraumNotSelected: 'Es muss zuerst ein Seminarraum ausgewählt werden.',
  seminarraumNotEingekauftForZeitraum: 'Der Seminarraum ist für die gewählte Zeit nicht eingekauft.',
  selectionHasConflicts: 'Auswahl kollidiert mit bestehenden Events.',
  selectionExceedsAblaufTage: 'Auswahl würde die Anzahl an Tagen im Ablauf übersteigen.',
  selectionOver15Days: 'Der Ablauf der gesamten Auswahl ist länger als 15 Tage.',
  experteOutsideGueltigkeit:
    'Trainer:in ist im gewählten Zeitraum für dieses Produkt nicht gültig. Bitte am Veranstaltungstyp (SAP VM) prüfen.',
  noPcRaumSelectedForPcSeminar: 'Für PC-Seminare dürfen nur PC-Räume gebucht werden.',
  pcRaumSelectedForNonPcSeminar: 'PC-Räume dürfen nur für PC-Seminare gebucht werden.',
  experteTerminGeloescht: 'Laut Hive wurde dieser Termin gelöscht. Bitte umplanen oder herausnehmen.',
};

type EventCheckResult = {
  state: CalendarEventState;
  message?: string;
};

export function removeEvent(events: readonly CalendarEvent[], eventId: string): readonly CalendarEvent[] {
  const eventToRemove = events.find((event) => event.id === eventId);
  if (eventToRemove === undefined) {
    console.error('Event to remove not found');
    return events;
  }

  const shouldRemoveGruppenraeume = isSeminarraumEvent(eventToRemove);

  const filteredSelection = events.filter((event) => {
    const notEventToRemove = event.id !== eventId;
    const notGruppenraumToRemove = !(shouldRemoveGruppenraeume && isGruppenraumEvent(event));

    return notEventToRemove && notGruppenraumToRemove;
  });

  return filteredSelection;
}

export function isResourceSelectedAtClickedDate(
  events: readonly CalendarEvent[],
  ressourceId: string,
  clickedDate: Date,
): boolean {
  return events.some((event) => event.resource === ressourceId && isDateInZeitraum(event, clickedDate));
}

export function createEventsForClickedResource(
  clickedDate: Date,
  clickedResource: AbstractCalendarResource,
  ablauf: readonly Zeitraum[],
  events: readonly CalendarEvent[],
  view: 'week' | 'day',
): CalendarEvent[] {
  const newEvents: CalendarEvent[] = [];

  switch (clickedResource.eventCreationStrategy) {
    case eventCreationStrategies.SINGLE_EVENT_SPANNING_ALL_ABLAUFTAGE: {
      const zeitraum = getZeitraeumeStartEnd(ablauf);
      const newEvent = clickedResource.createNewCalendarEventByInterval(zeitraum);

      newEvents.push(newEvent);
      break;
    }

    case eventCreationStrategies.SEPARATE_EVENTS_FOR_ALL_ABLAUFTAGE: {
      const selectedEventsOfClickedResource = events.filter((event) => event.resource === clickedResource.id);
      const resourceIsSelected = selectedEventsOfClickedResource.length > 0;
      if (resourceIsSelected) {
        const ablaufTag = ablauf.find((zeitraum) => isDateInZeitraum(zeitraum, clickedDate));
        if (ablaufTag === undefined) {
          console.error('No AblaufTag found for day', clickedDate);
          break;
        }

        const event = clickedResource.createNewCalendarEventByInterval(ablaufTag);

        newEvents.push(event);
      } else {
        for (const zeitraum of ablauf) {
          const event = clickedResource.createNewCalendarEventByInterval(zeitraum);

          newEvents.push(event);
        }
      }

      break;
    }

    default: {
      const exhaustiveCheck: never = clickedResource.eventCreationStrategy;
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unsupported action "${exhaustiveCheck}"`);
    }
  }

  for (const event of newEvents) {
    event.editable = true;
    event.resize = event.type === 'experte' && view === 'day';
    event.allDay = view === 'week';
  }

  return newEvents;
}

export function removeConflictingEvents(resource: AbstractCalendarResource, events: CalendarEvent[]): CalendarEvent[] {
  // Standort and Raum events are mutually exclusive, so we just remove them from the list of pending events
  if (isStandortResource(resource)) {
    return events.filter((event) => !isSeminarraumEvent(event) && !isGruppenraumEvent(event));
  }

  // only one seminarraum event is allowed
  if (isSeminarraumResource(resource)) {
    const filteredEvents: CalendarEvent[] = [];
    for (const event of events) {
      if (event.type === calendarResourceTypes.STANDORT) {
        continue;
      }

      if (event.type === calendarResourceTypes.SEMINARRAUM) {
        continue;
      }

      if (event.type === calendarResourceTypes.GRUPPENRAUM && event.locationKuerzel !== resource.location.kuerzel) {
        continue;
      }

      filteredEvents.push(event);
    }

    return filteredEvents;
  }

  return events;
}

export function addValidationErrors(
  pendingEvents: CalendarEvent[],
  fixedEvents: readonly CalendarEvent[],
  istTerminreihe: boolean,
  isPcCenterThema: boolean,
  seminarraumResources: readonly CalendarSeminarraumResource[],
  experteResources: readonly CalendarExperteResource[],
) {
  const selectedSeminarraumEvent = pendingEvents.find((event) => isSeminarraumEvent(event));
  const seminarraumResource = selectedSeminarraumEvent
    ? seminarraumResources.find((res) => res.id === selectedSeminarraumEvent.resource)
    : undefined;

  if (seminarraumResource && !isSeminarraumResource(seminarraumResource)) {
    throw new Error('Type of seminarraumResource is not CalendarSeminarraumResource');
  }

  for (const event of pendingEvents) {
    const validators = [
      () => checkGeloeschterExperteTermin(event),
      () => checkPcRaumOnlyForPcSeminare(event, seminarraumResource, isPcCenterThema),
      () => checkIncludesWeekend(event),
      () => checkGruppenraumMatchesRaumlocation(event, selectedSeminarraumEvent),
      () => checkEventCollisions(event, fixedEvents),
      () => checkSeminarraumAvailability(event, seminarraumResource),
      () => checkTerminreiheTooLong(pendingEvents, istTerminreihe),
      () => checkExperteInsideOfGueltigkeit(event, experteResources),
    ];

    for (const validator of validators) {
      const result = validator();
      event.state = result.state;
      event.message = result.message;
      if (result.state !== calendarEventStates.DEFAULT) {
        break;
      }
    }
  }
}

function checkGeloeschterExperteTermin(event: CalendarEvent): EventCheckResult {
  if (isExperteEvent(event) && event.blockungData.geloescht) {
    return {
      message: errors.experteTerminGeloescht,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkIncludesWeekend(event: CalendarEvent): EventCheckResult {
  const allDaysInEventInterval = eachDayOfInterval({ start: event.start, end: event.end });
  if (allDaysInEventInterval.some((day) => isWeekend(day))) {
    return {
      message: errors.containsWeekend,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkEventCollisions(event: CalendarEvent, fixedEvents: readonly CalendarEvent[]): EventCheckResult {
  const fixedEventsToCheckAgainst = fixedEvents.filter(
    (fixedEvent) => !isStandortEvent(fixedEvent) && !fixedEvent.isOldSelection && !isExperteEvent(fixedEvent),
  );

  if (
    fixedEventsToCheckAgainst.some(
      (fixedEvent) => event.resource === fixedEvent.resource && areIntervalsOverlapping(event, fixedEvent),
    )
  ) {
    return {
      message: errors.selectionHasConflicts,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkSeminarraumAvailability(
  event: CalendarEvent,
  seminarraumResource: CalendarSeminarraumResource | undefined,
): EventCheckResult {
  if (!seminarraumResource) {
    return { state: calendarEventStates.DEFAULT };
  }

  if (isSeminarraumEvent(event) && !seminarraumResource.isAvailable(event)) {
    return {
      message: errors.seminarraumNotEingekauftForZeitraum,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkPcRaumOnlyForPcSeminare(
  event: CalendarEvent,
  seminarraumResource: CalendarSeminarraumResource | undefined,
  isPcCenterThema: boolean,
): EventCheckResult {
  if (!seminarraumResource || !isSeminarraumEvent(event)) {
    return { state: calendarEventStates.DEFAULT };
  }

  if (isPcCenterThema && seminarraumResource.location.art !== locationArt.PC_CENTER) {
    return {
      message: errors.noPcRaumSelectedForPcSeminar,
      state: calendarEventStates.ERROR,
    };
  }

  if (!isPcCenterThema && seminarraumResource.location.art === locationArt.PC_CENTER) {
    return {
      message: errors.pcRaumSelectedForNonPcSeminar,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkGruppenraumMatchesRaumlocation(
  event: CalendarEvent,
  selectedRaumEvent: CalendarRaumEvent | undefined,
): EventCheckResult {
  if (!selectedRaumEvent && isGruppenraumEvent(event)) {
    return { message: errors.seminarraumNotSelected, state: calendarEventStates.ERROR };
  }

  if (isGruppenraumEvent(event) && selectedRaumEvent && selectedRaumEvent.locationKuerzel !== event.locationKuerzel) {
    return {
      message: errors.gruppenraumSeminarraumMismatch(selectedRaumEvent.locationKuerzel),
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkTerminreiheTooLong(pendingEvents: readonly CalendarEvent[], isTerminreihe: boolean): EventCheckResult {
  if (isTerminreihe) {
    // check if pendingEvents earliest and latest are > 15 days apart
    const { start, end } = getZeitraeumeStartEnd(pendingEvents);
    if (Math.abs(differenceInCalendarDays(start, end)) >= 15) {
      return {
        message: errors.selectionOver15Days,
        state: calendarEventStates.WARNING,
      };
    }
  }

  return { state: calendarEventStates.DEFAULT };
}

function checkExperteInsideOfGueltigkeit(
  event: CalendarEvent,
  experteResources: readonly CalendarExperteResource[],
): EventCheckResult {
  if (!isExperteEvent(event)) {
    return { state: calendarEventStates.DEFAULT };
  }

  const experteRessource: CalendarExperteResource | undefined = experteResources.find(
    (resource) => resource.id === event.resource,
  );

  const gueltigkeiten = experteRessource?.gueltigkeitenToEnforce() ?? [];
  if (gueltigkeiten.length === 0) {
    return { state: calendarEventStates.DEFAULT };
  }

  const isInsideGueltigkeiten = gueltigkeiten.some(
    (gueltigkeit) => gueltigkeit.start <= event.start && gueltigkeit.end >= event.end,
  );

  if (!isInsideGueltigkeiten) {
    return {
      message: errors.experteOutsideGueltigkeit,
      state: calendarEventStates.ERROR,
    };
  }

  return { state: calendarEventStates.DEFAULT };
}

export function setEventTimesFromAblauf(events: CalendarEvent[], ablauf: Zeitraum[]): void {
  const ablaufZeitraum = getZeitraeumeStartEnd(ablauf);
  for (const event of events) {
    switch (event.type) {
      case calendarResourceTypes.EXPERTE: {
        break;
      }

      case calendarResourceTypes.SEMINARRAUM:
      case calendarResourceTypes.STANDORT: {
        event.start = ablaufZeitraum.start;
        event.end = ablaufZeitraum.end;
        break;
      }

      case calendarResourceTypes.GRUPPENRAUM: {
        const zeitraum = ablauf.find(({ start }) => isSameDay(start, event.start));
        if (!zeitraum) {
          throw new Error('AblaufTag für Ressource nicht gefunden');
        }

        event.start = zeitraum.start;
        event.end = zeitraum.end;
      }
    }
  }
}
